import { ApolloError, ApolloLink, type Operation } from "@apollo/client";
import { RetryLink } from "@apollo/client/link/retry";

// Pass as `context` to a read query that should survive one transient upstream failure.
export const RETRY_TRANSIENT = { retryTransient: true } as const;
const RETRYABLE_HTTP_STATUS = new Set([500, 502, 503, 504]);

const wantsRetry = (operation: Operation) => operation.getContext()["retryTransient"] === true;
const isTransientFailure = (errors: readonly { message: string; extensions?: Record<string, unknown> }[] | undefined) =>
    Boolean(
        errors?.length &&
            errors.every((error) => {
                const exception = error.extensions?.["exception"];
                return (
                    error.extensions?.["code"] === "INTERNAL_SERVER_ERROR" ||
                    (typeof exception === "object" &&
                        exception !== null &&
                        "code" in exception &&
                        exception.code === 14) ||
                    error.message.startsWith("14 UNAVAILABLE:")
                );
            }),
    );

export const createTransientRetryLink = (): ApolloLink => {
    const retried = new WeakSet<Operation>();
    const retry = new RetryLink({
        delay: { initial: 500, max: 500, jitter: false },
        attempts: (count, operation, error: unknown) => {
            if (count >= 2 || !wantsRetry(operation)) return false;
            const status = error instanceof Error && "statusCode" in error ? error.statusCode : undefined;
            const connectionFailure =
                error instanceof TypeError || (typeof status === "number" && RETRYABLE_HTTP_STATUS.has(status));
            if (!(error instanceof ApolloError && isTransientFailure(error.graphQLErrors)) && !connectionFailure)
                return false;
            retried.add(operation);
            return true;
        },
    });
    // The BFF exposes upstream UNAVAILABLE as either internal errors or raw gRPC errors.
    // Preserve the final response for normal error handling after one retry.
    const graphqlFailure = new ApolloLink((operation, forward) =>
        forward(operation).map((result) => {
            if (wantsRetry(operation) && !retried.has(operation) && result.errors && isTransientFailure(result.errors))
                throw new ApolloError({ graphQLErrors: result.errors });
            return result;
        }),
    );
    return ApolloLink.from([retry, graphqlFailure]);
};
